/**
 * DateRangeFilter — inline quick-range buttons with optional custom dates and comparison.
 * @layer atom
 * @package @plerity/ui-core
 *
 * The one implementation behind every "7d / 14d / 28d / 90d / YTD / Custom" bar in
 * the dashboards. Distinct from `DateRangePicker` (the calendar-popover picker over
 * react-aria): this is the always-visible filter-bar control. Dependency-free (no
 * react-aria), so it is safe in eager chunks.
 *
 * Generic value contract `{ start, end, preset }` with ISO 'YYYY-MM-DD' strings. Apps
 * with their own value shape adapt at the edge (see `@plerity/dashboard-shared`'s
 * `DateRangePicker`, which maps `{ startDate, endDate, comparison* }` onto this).
 *
 * Literal Tailwind ramp with a `dark:` pair for every colour (no CSS vars: the SPA
 * Tailwind v4 build strips them), no `-950` shades.
 */

import { type ReactNode, useCallback, useMemo, useState } from "react";
import { cn } from "../utils/cn";
import type { DateRangeFilterPreset, DateRangeFilterProps, DateRangeFilterValue } from "./DateRangeFilter.types";
import { clampIso, formatRangeLabel, lastDaysRange, yearToDateRange } from "./dateRangeMath";

/** `value.preset` when the dates were typed in rather than picked from a preset. */
export const CUSTOM_PRESET = "Custom";

/** The fleet's historical presets: 7d, 14d, 28d, 90d, 12mo, YTD. */
export const DEFAULT_DATE_RANGE_PRESETS: DateRangeFilterPreset[] = [
  { key: "7d", days: 7 },
  { key: "14d", days: 14 },
  { key: "28d", days: 28 },
  { key: "90d", days: 90 },
  { key: "12mo", days: 365 },
  { key: "YTD", ytd: true },
];

/** Resolve a preset to its inclusive ISO window against `today`. */
export function resolvePreset(preset: DateRangeFilterPreset, today: Date = new Date()) {
  if (preset.range) return preset.range(today);
  if (preset.ytd) return yearToDateRange(today);
  return lastDaysRange(preset.days ?? 1, today);
}

const BUTTON_BASE =
  "rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500";
const BUTTON_ACTIVE = "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200";
const BUTTON_IDLE = "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700";
// border-control: tokens-apps form-control border, WCAG 1.4.11 >= 3:1 in both
// themes (the old gray-300 / dark:gray-600 pair measured ~1.3-1.5:1).
const DATE_INPUT =
  "rounded-md border border-control bg-white px-2 py-1 text-xs text-gray-900 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-gray-800 dark:text-gray-100";

function CalendarGlyph() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-gray-500 dark:text-gray-400"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

export function DateRangeFilter({
  value,
  onChange,
  presets = DEFAULT_DATE_RANGE_PRESETS,
  customInputs = "toggle",
  minDate,
  maxDate,
  enforceOrder = false,
  compare,
  showPeriodLabel = true,
  layout = "horizontal",
  ariaLabel = "Date range",
  today,
  customPresetKey = CUSTOM_PRESET,
  className,
}: DateRangeFilterProps): ReactNode {
  const [customOpen, setCustomOpen] = useState(value.preset === customPresetKey);

  // Only the toggle mode's open Custom button competes with the presets for
  // "pressed"; in `always` mode the inputs sit beside the presets and the
  // active preset must stay highlighted (it un-highlights on its own once a
  // date is typed, because the value's preset becomes `customPresetKey`).
  const customPanelOpen = customInputs === "toggle" && customOpen;
  const showInputs = customInputs === "always" || customPanelOpen;

  const pick = useCallback(
    (preset: DateRangeFilterPreset) => {
      const { start, end } = resolvePreset(preset, today);
      onChange({ start, end, preset: preset.key });
      setCustomOpen(false);
    },
    [onChange, today]
  );

  const edit = useCallback(
    (field: "start" | "end", raw: string) => {
      let next: DateRangeFilterValue = { ...value, [field]: clampIso(raw, minDate, maxDate), preset: customPresetKey };
      if (enforceOrder && next.start && next.end && next.start > next.end) {
        next = field === "start" ? { ...next, end: next.start } : { ...next, start: next.end };
      }
      onChange(next);
    },
    [customPresetKey, enforceOrder, maxDate, minDate, onChange, value]
  );

  const periodLabel = useMemo(() => formatRangeLabel(value.start, value.end), [value.start, value.end]);
  const compareLabel = useMemo(
    () => (compare?.enabled && compare.range ? formatRangeLabel(compare.range.start, compare.range.end) : ""),
    [compare?.enabled, compare?.range]
  );

  const compact = layout === "compact";

  return (
    <div className={cn("flex", compact ? "flex-col gap-2" : "flex-wrap items-center gap-3", className)}>
      {/* flex-wrap: the preset row must wrap inside narrow hosts (audit-ui filter
          bar in a ~229px card at 375px clipped "Custom" off-screen, 2026-10-30). */}
      <fieldset aria-label={ariaLabel} className="m-0 flex min-w-0 flex-wrap items-center gap-1 border-0 p-0">
        <CalendarGlyph />
        {presets.map((preset) => {
          const active = value.preset === preset.key && !customPanelOpen;
          return (
            <button
              key={preset.key}
              type="button"
              aria-pressed={active}
              onClick={() => pick(preset)}
              className={cn(BUTTON_BASE, active ? BUTTON_ACTIVE : BUTTON_IDLE)}
            >
              {preset.label ?? preset.key}
            </button>
          );
        })}
        {customInputs === "toggle" && (
          <button
            type="button"
            aria-pressed={customOpen}
            onClick={() => setCustomOpen((open) => !open)}
            className={cn(BUTTON_BASE, customOpen ? BUTTON_ACTIVE : BUTTON_IDLE)}
          >
            Custom
          </button>
        )}
      </fieldset>

      {showInputs && (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <input
            type="date"
            value={value.start}
            min={minDate}
            max={enforceOrder && value.end ? (maxDate && maxDate < value.end ? maxDate : value.end) : maxDate}
            onChange={(event) => edit("start", event.target.value)}
            aria-label="Start date"
            className={DATE_INPUT}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">to</span>
          <input
            type="date"
            value={value.end}
            min={enforceOrder && value.start ? (minDate && minDate > value.start ? minDate : value.start) : minDate}
            max={maxDate}
            onChange={(event) => edit("end", event.target.value)}
            aria-label="End date"
            className={DATE_INPUT}
          />
        </div>
      )}

      {compare && (
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            checked={compare.enabled}
            onChange={() => compare.onToggle(!compare.enabled)}
            className="h-3.5 w-3.5 rounded border-control text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-500"
          />
          {compare.label ?? "Compare"}
        </label>
      )}

      {showPeriodLabel && periodLabel && (
        <span className="text-xs text-gray-500 dark:text-gray-400">{periodLabel}</span>
      )}

      {compareLabel && <span className="text-xs text-gray-500 dark:text-gray-400">vs {compareLabel}</span>}
    </div>
  );
}

DateRangeFilter.displayName = "DateRangeFilter";
